// @flow
/** @jsx isolateComponent */

import _ from 'lodash';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Zindexes } from '@zapier/theme';
import {
  MessageBar,
  type MessageBarTypes,
  Timeout,
} from '@zapier/common-components';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

const animTransform = 'translate(0, -75vh)';
const transitionDuration = 500;

const Styles = labelStyles(__filename, {
  root: {
    left: '50%',
    position: 'fixed',
    top: '65px',
    transform: 'translate(-50%)',
    zIndex: Zindexes.pageNotification,

    '.notification-enter': {
      transform: animTransform,
    },
    '.notification-exit': {
      transform: animTransform,
    },
  },
  notification: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '10px',
    maxWidth: '90vw',
    transitionDuration: `${transitionDuration}ms`,
    transitionProperty: 'transform',
    transitionTimingFunction: 'ease-in-out',
    width: '700px',
    zIndex: Zindexes.pageNotification,
  },
  message: {
    flex: 1,
    lineHeight: 1.4,
    maxHeight: '80vh',
    overflowWrap: 'break-word',
    overflowY: 'auto',
    whiteSpace: 'pre-line',
  },
});

type Notification = {
  id: number,
  message: string,
  type: MessageBarTypes,
};

type NotificationDismissProps = {
  dismissAfter: number,
  onDismiss: (id?: number) => void,
};

type PageNotificationProps = Notification & NotificationDismissProps;

type PageNotificationWrapperProps = NotificationDismissProps & {
  notifications: Notification[],
};

const PageNotification = (props: PageNotificationProps) => {
  const notification = (
    <div css={Styles.notification}>
      <MessageBar
        fitToScreen={true}
        onDismiss={() => props.onDismiss(props.id)}
        type={props.type}
      >
        <div css={Styles.message}>{props.message}</div>
      </MessageBar>
    </div>
  );
  return props.dismissAfter === Infinity ? (
    notification
  ) : (
    <Timeout
      delay={props.dismissAfter}
      isWaiting={true}
      onTimeout={() => props.onDismiss(props.id)}
    >
      {notification}
    </Timeout>
  );
};

const PageNotificationWrapper = (props: PageNotificationWrapperProps) => {
  return (
    <div css={Styles.root}>
      <TransitionGroup>
        {_.map(props.notifications, notification => {
          return (
            !!notification.message && (
              <CSSTransition
                classNames="notification"
                key={notification.id}
                timeout={{
                  enter: transitionDuration,
                  exit: transitionDuration,
                }}
              >
                <PageNotification
                  dismissAfter={props.dismissAfter}
                  onDismiss={props.onDismiss}
                  {...notification}
                />
              </CSSTransition>
            )
          );
        })}
      </TransitionGroup>
    </div>
  );
};

PageNotificationWrapper.defaultProps = {
  dismissAfter: 5000,
};

export default PageNotificationWrapper;
